export const onboardingCopy = {
  signup: {
    success:
      'Your trial shop is ready. You have ₹50.00 to test how ChatLogix answers customer questions. Nothing is sent to your real WhatsApp yet.',
    form: {
      phone: {
        title: 'Try it on your catalog',
        subtext:
          'Enter your mobile number to create a demo shop and try the assistant on your products.',
        fieldLabel: 'Mobile number',
        helper: '₹50 free trial. No card.',
        cta: 'Send code',
        footnote: 'Nothing is sent to your customers. You try the assistant first.',
      },
      otp: {
        title: 'Enter the verification code',
        subtext: 'Sent to {phone}. Then you can set up your store and test the assistant.',
        cta: 'Verify and start',
      },
      footerLogin: 'Already have a shop?',
      footerLoginLink: 'Log in',
    },
    legal: {
      agree: 'By continuing, you agree to our',
      and: 'and',
      terms: 'Terms of Service',
      privacy: 'Privacy Policy',
      termsUrl: 'https://www.chatlogix.in/terms',
      privacyUrl: 'https://www.chatlogix.in/privacy',
    },
    meta: {
      title: 'Try it on your catalog | ChatLogix',
      description:
        'Try your WhatsApp assistant on your catalog. ₹50 free trial. No card. Customers are not messaged.',
    },
    marketing: {
      hero: {
        kicker: 'TRY IT BEFORE YOU PAY',
        h1: 'Try your WhatsApp assistant on your catalog — before you pay.',
        subhead:
          'Upload your catalog, then chat like a customer with text and voice. See how it replies before you connect WhatsApp.',
      },
      preview: {
        title: 'What you try in the demo',
        badge: 'Demo only',
        chatName: 'Your shop assistant',
        status: 'Online',
        composer: 'Ask about your products…',
        caption: 'You check a product before you sell it. Do the same with this assistant.',
        steps: [
          { title: 'Your catalog', body: 'Upload your products' },
          { title: 'AI conversations', body: 'Ask like a customer' },
          { title: 'Voice & text', body: 'See how it replies' },
        ],
        customerText: 'Almond cookies — stock irukka?',
        assistantText: 'Yes — ₹180 a box of 12. Fresh batch today.',
        voiceLabel: 'Voice note',
        voiceDuration: '0:07',
        assistantFollowUp: 'I can note 2 boxes for this evening. Shall I?',
      },
      problem: {
        h2: 'Why shop owners struggle with WhatsApp every day',
        bullets: [
          {
            title: 'Same questions, every day',
            body: '"Rate enna?", "Delivery irukka?", "Stock irukka?" — repeated questions eat hours you could spend on your work.',
          },
          {
            title: 'Missed messages = missed sales',
            body: 'When you\'re busy in the kitchen, on the shop floor, or on the road, customers move to the next seller.',
          },
          {
            title: 'Late-night typing',
            body: 'Replying manually after a long day instead of resting or growing the business.',
          },
        ],
      },
      solution: {
        h2: 'How ChatLogix automates your WhatsApp customer service',
        bullets: [
          {
            title: 'Smart automatic replies on WhatsApp',
            body: 'Answers like a trained shop staff member, using your products and prices.',
          },
          {
            title: 'WhatsApp order support built in',
            body: 'Customers ask; your assistant guides them toward a clear order.',
          },
          {
            title: 'Try it safely before going live',
            body: 'Test the **same AI** in a private demo. **Your real WhatsApp customers are not messaged** during trial.',
          },
        ],
      },
      trust: [
        'No card required',
        'Customers are not messaged during the trial',
        'Finish setup anytime — no waiting on us',
      ],
    },
    faq: {
      h2: 'Common questions',
      items: [
        {
          question: 'What is a WhatsApp shop assistant?',
          answer:
            'A tool that reads customer messages on WhatsApp and replies with your prices, products, and order details — automatically, in a natural way.',
        },
        {
          question: 'Is the ₹50 trial really free?',
          answer:
            'Yes. Sign up with your mobile number. No card needed. You get ₹50 of free trial balance to upload your catalog and test the assistant before connecting live WhatsApp.',
        },
        {
          question: 'Will my customers get messages during the trial?',
          answer:
            'No. The demo runs in a safe test environment. Live WhatsApp connects when you finish setup and connect your number.',
        },
        {
          question: 'Who is ChatLogix for?',
          answer:
            'Home businesses, bakeries, tailors, kirana sellers, and any small shop that gets orders and questions on WhatsApp.',
        },
      ],
    },
  },
  login: {
    marketing: {
      h1: 'Your shop assistant is still running.',
      subhead: 'Sign in to pick up catalog, chats, and orders where you left off.',
    },
    form: {
      title: 'Welcome back',
      subtext: 'Sign in to continue managing your business.',
      otpTitle: 'Verify your phone',
      otpSubtext: 'Enter the verification code sent to your phone',
      cta: 'Send code',
      footerSignup: 'New here?',
      footerSignupLink: 'Try it on your catalog',
    },
  },
  step1: {
    headline: 'Your shop',
    explainer: 'Add the shop details the assistant should use when customers ask about pickup or delivery.',
    trustLine: 'You can change this later.',
    whyWeAsk: 'The assistant uses your shop name and address when customers ask about pickup or delivery.',
    addressLabel: 'Shop address',
    deliveryNotesLabel: 'Delivery notes (optional)',
    fieldNotes: {
      businessName: 'Used in greetings and order confirmations.',
      ownerName: 'If a customer asks who runs the shop.',
      email: 'Where we send account updates. Optional until you finish setup.',
      city: 'Helps answer “do you deliver to my area?”',
      address: 'Quoted for pickup and where orders ship from.',
      deliveryNotes: 'Delivery areas, charges, and hours.',
      category: 'So the assistant talks like your kind of shop.',
    },
  },
  step2: {
    headline: 'What you sell',
    explainer: 'Upload a menu photo or spreadsheet first — we read your products and shop details from it.',
    skipNote: 'You can add products later. Demo answers may be limited until then.',
    uploading: 'Uploading…',
    reading: 'Reading your menu…',
    readingPage: 'Reading page {page} of {total}… found {count} items',
    readingWait: 'This can take a minute.',
    reviewHeadline: 'Check your products',
    reviewExplainer: 'Fix names, descriptions, and prices before the assistant uses them.',
    found: 'Check anything that looks wrong, then save. Demo catalogs are limited to 10 products.',
    descriptionLabel: 'Description',
    descriptionNote: 'We write a full listing from your catalog. Edit anything that is wrong.',
    descriptionPlaceholder: 'What it is, pack size, when people order it…',
    maxProducts: 'Demo is limited to 10 products. Remove one to add another.',
    foundNone: 'We could not read products from that file. Try another photo below, or add items by hand.',
    tryAnother: 'Upload a menu photo or spreadsheet',
    continueUpload: 'Read this file',
    takePhoto: 'Take photo',
    uploadFile: 'Upload file',
    uploadExample: 'A clear menu photo or a price-list spreadsheet.',
    addByHand: 'Add 3 items by hand',
    skipWarning: 'Without products, the assistant cannot quote prices.',
    addProduct: 'Add a product',
    edit: 'Edit',
    done: 'Done',
    remove: 'Remove',
    nameLabel: 'Name',
    priceLabel: 'Price (₹)',
    unitLabel: 'Unit',
    qtyLabel: 'Qty',
    pricingSection: 'Pricing & units',
    baseQtyHint: 'Quantity for base price',
    basePriceHint: 'Price for base quantity (in rupees)',
    isDivisibleLabel: 'Allow fractional quantities',
    isDivisibleHint: 'Customers can order 0.5kg, 2.5 pieces, etc.',
    minQtyLabel: 'Minimum order quantity',
    maxQtyLabel: 'Maximum order quantity',
    missingPrice: 'Add a price so the assistant can quote this item.',
    save: 'Save products & continue',
    skip: 'Skip for now',
    whyWeAsk: 'Your assistant can only quote prices and stock from products you add.',
    guide: {
      kicker: 'Catalog',
      h1: 'Teach the assistant what you sell',
      subhead: 'Upload a menu photo or spreadsheet. We read products and prices from it.',
      actionTitle: 'Upload your menu',
      howTo: [
        { title: 'Clear photo', body: 'Menu, brochure, or price list. Avoid blur and glare.' },
        { title: 'Or a spreadsheet', body: 'Name, price, and unit in columns works best.' },
        { title: 'Check before you save', body: 'Fix names and prices. Demo is limited to 10 items.' },
      ],
      footnote: 'Nothing is sent to your customers. You can skip and add products later.',
      preview: {
        chatName: 'Your shop assistant',
        status: 'Online',
        customerText: 'Almond cookies, stock irukka?',
        assistantText: 'Yes, ₹180 a box of 12. Fresh batch today.',
      },
    },
    reviewGuide: {
      kicker: 'Catalog',
      h1: 'Check names and prices',
      subhead: 'Fix anything that looks wrong before the assistant uses it in chat.',
      actionTitle: 'Review your products',
      howTo: [
        { title: 'Names', body: 'Use the name a customer would type on WhatsApp.' },
        { title: 'Prices', body: 'Add a price so the assistant can quote this item.' },
        { title: 'Save when it looks right', body: 'You can edit products again from Confirm.' },
      ],
      footnote: 'Demo catalogs are limited to 10 products.',
    },
  },
  step3: {
    headline: 'How it sounds',
    explainer: 'The first message a customer sees.',
    whyWeAsk: 'This greeting is the first thing a customer sees. Keep it short and in their language.',
  },
  step4: {
    headline: 'Try a customer chat',
    explainer: 'Private demo. Nothing is sent to your real WhatsApp.',
    trustBanner: 'Demo only — your customers are not messaged',
    loading: 'Replying…',
    emptyWallet:
      'Your trial balance is used up. You can still finish setup and top up from your wallet later.',
    suggestedPrompts: ['What are your prices?', 'I want to order 2 kg', 'Do you deliver?'],
    whyWeAsk: 'Demo chat spends your trial balance so you can see reply quality and cost before going live.',
    guide: {
      kicker: 'Demo',
      h1: 'Try a customer chat',
      actionTitle: 'Chat with your assistant',
      subhead: 'Private demo. Nothing is sent to your real WhatsApp.',
      howTo: [],
    },
  },
  step5: {
    headline: 'Finish setup',
    explainer: 'You go straight to your dashboard. Connect WhatsApp when you are ready.',
    submitCta: 'Finish setup',
    promise: 'Your trial balance carries forward. Top up anytime from your wallet.',
    whyWeAsk: 'One last check that your shop name and email look right before you open the dashboard.',
    guide: {
      kicker: 'Finish',
      h1: 'Ready to open your shop dashboard',
      actionTitle: 'Finish setup',
      subhead: 'You land on your dashboard right away — connect WhatsApp when you are ready.',
      howTo: [
        { title: 'We save your details', body: 'Name, catalog, and greeting are ready for customers.' },
        { title: 'Trial balance stays', body: 'Whatever is left from your ₹50 trial comes with you.' },
        { title: 'Connect WhatsApp next', body: 'From your dashboard when you want to go live.' },
      ],
      footnote: 'Email is for account updates. Live customers are not messaged until you connect WhatsApp.',
    },
  },
  clarify: {
    headline: 'A quick detail',
    explainer: 'These answers help the assistant quote the right prices and delivery rules.',
    progressBuilding: 'About {pct}% ready for demo — a few more details make replies accurate.',
    progressReady: 'We know enough to try the demo — one more detail helps if you have time.',
    progressMeter: 'Detail {current} of {total}',
    whyDelivery: 'This helps quote delivery correctly.',
    whyHours: 'This helps answer when you are open.',
    whyPrice: 'This helps quote the right prices.',
    whyDefault: 'This helps the assistant reply accurately.',
    continue: 'Continue',
    enough: "That's enough — review my shop",
    answerRequired: 'Pick or type an answer, or tap That\'s enough.',
    textPlaceholder: 'Type your answer…',
    guide: {
      kicker: 'Details',
      h1: 'A few answers make replies accurate',
      actionTitle: 'A quick detail',
      subhead: 'We ask one question at a time for anything the file did not cover.',
      howTo: [
        { title: 'Pick or type', body: 'Choose a chip or type a short answer.' },
        { title: 'One at a time', body: 'Hours, delivery, or prices we could not read.' },
        { title: "You can stop", body: "Tap That's enough when you want to review the shop." },
      ],
      footnote: 'These answers help quote the right prices and delivery rules.',
    },
  },
  confirm: {
    headline: 'Check your shop details',
    explainer: 'We filled these from your catalog. Fix anything that looks wrong.',
    shopName: 'Shop name',
    shopFacts: 'Shop facts',
    howItSounds: 'How it sounds',
    emailOptional: 'Where should we send updates?',
    emailNote: 'Optional now. We use this for account updates, not customer messages.',
    city: 'City (optional)',
    category: 'Category',
    assistantName: 'Assistant name',
    greeting: 'First message customers see',
    personality: 'How the assistant should sound',
    personalityOverride: 'Adjust the wording (optional)',
    personalityChips: ['Friendly', 'Formal', 'Short replies', 'Warm'],
    productSummary: '{count} products saved',
    noProducts: 'No products yet — you can still try the demo',
    editCatalog: 'Edit products',
    save: 'Save & try demo chat',
    guide: {
      kicker: 'Confirm',
      h1: 'Check the details customers will hear',
      actionTitle: 'Your shop details',
      subhead: 'We filled these from your catalog. Fix anything that looks wrong.',
      howTo: [
        { title: 'Shop name and address', body: 'Quoted for pickup and where orders ship from.' },
        { title: 'Greeting', body: 'The first message a customer sees. Keep it short.' },
        { title: 'Category', body: 'So the assistant talks like your kind of shop.' },
      ],
      footnote: 'You can change this later from your dashboard.',
    },
  },
  demoPhone: {
    online: 'online',
    typing: 'typing…',
    today: 'Today',
    composerPlaceholder: 'Type a message',
    demoOnly: 'Demo only',
    demoHonesty: 'Demo — not sent to customers',
    recording: 'Recording {sec}s',
    slideCancel: 'Slide to cancel',
    releaseSend: 'Release to send',
    micDenied: 'Microphone blocked. Choose a voice file instead.',
    voiceFallback: 'Voice note',
    lastReplyCost: 'Last reply {cost}',
    continue: 'Continue to review',
    attachLabel: 'Attach',
    sendLabel: 'Send',
    micLabel: 'Hold to record',
    videoLabel: 'Video call',
    callLabel: 'Voice call',
    menuLabel: 'Menu',
  },
  review: {
    emailRequired: 'Add your email so we can send shop updates.',
    emailLabel: 'Where should we send updates?',
    emailMissing: 'Email not added yet',
    products: '{count} products',
    productOne: '1 product',
    noProducts: 'No products yet',
    greetingFallback: 'No greeting saved yet',
    editDetails: 'Edit',
  },
  submitted: {
    headline: "We're reviewing your shop",
    explainer:
      'A person is checking your details. This usually takes 1–2 business days. You can still edit or try the demo while you wait.',
    notifyAt: 'We will notify you at {phone}.',
    submittedOn: 'Submitted on {date}',
    timeline: {
      submitted: 'Submitted',
      inReview: 'In review',
      approved: 'Approved',
      connect: 'Connect WhatsApp',
    },
    whileYouWait: 'While you wait',
    tryDemo: 'Try three more demo questions',
    tryDemoHint: 'See how the assistant answers prices, stock, and delivery.',
    addProducts: 'Add products',
    addProductsHint: 'More items make demo answers more useful.',
    sharePartner: 'Share with a partner',
    sharePartnerHint: 'Copy a short note you can send them.',
    sharePartnerText:
      'I am setting up a WhatsApp shop assistant on ChatLogix. They are reviewing my shop this week.',
    shareCopied: 'Note copied',
    editApplication: 'Edit application',
    guide: {
      kicker: 'Submitted',
      h1: 'We are reviewing your shop',
      actionTitle: 'What happens next',
      subhead: 'A person checks your details. This usually takes 1–2 business days.',
      howTo: [
        { title: 'Review', body: 'Our team reads your shop details and catalog.' },
        { title: 'Notify', body: 'We message you on your phone when you are approved.' },
        { title: 'Go live', body: 'Then you can connect WhatsApp and load your wallet.' },
      ],
      footnote: 'You can still edit your application or try the demo while you wait.',
    },
  },
  rejected: {
    headline: 'We could not approve your shop yet',
    explainer: 'Read the note below, update your details, and message us if you need help.',
    reasonFallback: 'We could not share a specific reason. Message us and we will explain.',
    editApplication: 'Update shop details',
    addProducts: 'Review catalog',
    contactSupport: 'WhatsApp us',
    contactFallback: 'Contact us',
    contactFallbackUrl: 'https://www.chatlogix.in/contact',
    backToLogin: 'Log out',
    guide: {
      kicker: 'Application',
      h1: 'We could not approve your shop yet',
      actionTitle: 'What you can do',
      subhead: 'Update your details or message us. You are not stuck here.',
      howTo: [
        { title: 'Read the note', body: 'The reason below tells you what to fix.' },
        { title: 'Update details', body: 'Edit your shop or catalog, then submit again.' },
        { title: 'Ask for help', body: 'We help shops set up every day.' },
      ],
    },
  },
  wallet: {
    lowBalance: 'Your trial balance is running low.',
    expired: 'Your trial has expired. Finish setup and top up from your wallet when you need more.',
  },
  help: {
    prefix: 'Questions?',
    suffix: '— we help shops set up every day.',
    whatsapp: 'WhatsApp us',
    email: 'Email us',
    fallbackLabel: 'Contact us',
    fallbackUrl: 'https://www.chatlogix.in/contact',
    logout: 'Log out',
    loading: 'Loading…',
  },
} as const;
